import { useContext, useMemo, useSyncExternalStore } from 'react';
import FiltersContext from '@/context/FiltersContext/FiltersContext';
import { useLocalFiltersRegistry } from '@/context/LocalFiltersRegistry/LocalFiltersRegistry';
import { readCachedCountryCodes, RESOLVED_COUNTRY_CODES_KEY } from '@/models/filter/configs/general/countryFilter';
import { globalFilters, hcoGlobalFilters } from '@/models/filter/globalFilterAllowlist';
import type { ChatbotActiveFilters, ChatbotFilterValue } from '@/models/chatbot';
import type { SelectItem } from '@/types/ui';
import { SESSION_STORAGE_SYNC_EVENT } from '@/hooks/useSessionStorage';
import useChatbotStore from '../store/useChatbotStore';

let cachedResolvedCountryCodesRaw: string | null | undefined;
let cachedResolvedCountryCodes: ReadonlyArray<string> = [];

// Global (app-wide header) filters are restricted to this allowlist so page
// state never leaks into the global bucket. Page/local filters are forwarded
// separately under `localFilters` so the agent can scope to the user's page.
const GLOBAL_FILTER_ALLOWLIST = [...globalFilters, ...hcoGlobalFilters];

interface DateRangeFilterValue {
  beginn: unknown;
  end: unknown;
  period?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isDateRangeFilterValue(value: unknown): value is DateRangeFilterValue {
  return isRecord(value) && 'beginn' in value && 'end' in value;
}

function isSelectItem(value: unknown): value is SelectItem<unknown> {
  return isRecord(value) && 'value' in value && 'label' in value;
}

function isFormattableDate(value: unknown): value is { format: (template: string) => string } {
  return isRecord(value) && 'format' in value && typeof value.format === 'function';
}

function serializeDateValue(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }

  if (isFormattableDate(value)) {
    return value.format('YYYY-MM-DD');
  }

  return undefined;
}

function serializeFilterValue(value: unknown): ChatbotFilterValue | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (value === null) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'boolean') {
    return value ? true : undefined;
  }

  if (isDateRangeFilterValue(value)) {
    const beginn = serializeDateValue(value.beginn);
    const end = serializeDateValue(value.end);

    if (!beginn || !end) {
      return undefined;
    }

    return {
      beginn,
      end,
      ...(typeof value.period === 'string' ? { period: value.period } : {}),
    };
  }

  if (Array.isArray(value)) {
    const serializedValues = value
      .map(filterItem => serializeFilterValue(filterItem))
      .filter((serializedItem): serializedItem is ChatbotFilterValue => serializedItem !== undefined);

    return serializedValues.length > 0 ? serializedValues : undefined;
  }

  if (isSelectItem(value)) {
    if (typeof value.label === 'string') {
      return value.label;
    }

    if ('altLabel' in value && typeof value.altLabel === 'string') {
      return value.altLabel;
    }

    return serializeFilterValue(value.value);
  }

  if (!isRecord(value)) {
    return undefined;
  }

  const serializedEntries = Object.entries(value).reduce<Record<string, ChatbotFilterValue>>(
    (result, [key, entryValue]) => {
      const serializedValue = serializeFilterValue(entryValue);

      if (serializedValue !== undefined) {
        result[key] = serializedValue;
      }

      return result;
    },
    {},
  );

  return Object.keys(serializedEntries).length > 0 ? serializedEntries : undefined;
}

function serializeFilterMap(filters: Record<string, unknown>): Record<string, ChatbotFilterValue> {
  return Object.entries(filters).reduce<Record<string, ChatbotFilterValue>>((result, [key, value]) => {
    const serializedValue = serializeFilterValue(value);

    if (serializedValue !== undefined) {
      result[key] = serializedValue;
    }

    return result;
  }, {});
}

function getResolvedCountryCodesSnapshot(): ReadonlyArray<string> {
  const rawResolvedCountryCodes = sessionStorage.getItem(RESOLVED_COUNTRY_CODES_KEY);

  if (rawResolvedCountryCodes === cachedResolvedCountryCodesRaw) {
    return cachedResolvedCountryCodes;
  }

  cachedResolvedCountryCodesRaw = rawResolvedCountryCodes;
  cachedResolvedCountryCodes = readCachedCountryCodes();

  return cachedResolvedCountryCodes;
}

function subscribeToResolvedCountryCodes(onStoreChange: VoidFunction): VoidFunction {
  const handleResolvedCountryCodesSync = (event: Event) => {
    if (!(event instanceof CustomEvent) || !isRecord(event.detail) || event.detail.key !== RESOLVED_COUNTRY_CODES_KEY) {
      return;
    }

    onStoreChange();
  };

  window.addEventListener(SESSION_STORAGE_SYNC_EVENT, handleResolvedCountryCodesSync);

  return () => window.removeEventListener(SESSION_STORAGE_SYNC_EVENT, handleResolvedCountryCodesSync);
}

const useActiveFiltersForChatbot = (): ChatbotActiveFilters => {
  const { filterState } = useContext(FiltersContext);
  const { getAllLocalFilters, version } = useLocalFiltersRegistry();
  const isDrawerOpen = useChatbotStore(state => state.isDrawerOpen);
  const resolvedCountryCodes = useSyncExternalStore(
    subscribeToResolvedCountryCodes,
    getResolvedCountryCodesSnapshot,
    getResolvedCountryCodesSnapshot,
  );

  const serializedGlobalFilters = useMemo(() => {
    const allowlistedFilters = GLOBAL_FILTER_ALLOWLIST.reduce<Record<string, unknown>>((result, filterType) => {
      result[filterType] = filterState[filterType];
      return result;
    }, {});

    return serializeFilterMap(allowlistedFilters);
  }, [filterState]);

  const serializedLocalFilters = useMemo(
    () => serializeFilterMap(getAllLocalFilters()),
    [getAllLocalFilters, version, isDrawerOpen],
  );

  return useMemo(() => {
    return {
      ...(Object.keys(serializedGlobalFilters).length > 0 ? { globalFilters: serializedGlobalFilters } : {}),
      ...(Object.keys(serializedLocalFilters).length > 0 ? { localFilters: serializedLocalFilters } : {}),
      ...(resolvedCountryCodes.length > 0 ? { resolvedCountryCodes: [...resolvedCountryCodes] } : {}),
    };
  }, [serializedGlobalFilters, serializedLocalFilters, resolvedCountryCodes]);
};

export default useActiveFiltersForChatbot;
